import { formatPrice } from '@folio/lib';
import { isPerilValueIncluded } from './peril-value';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useHasPolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  assetControllerGetAssetExternalQuoteQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  type Asset,
  type KeyValueFieldObject,
} from 'korint-sdk';
import { CheckIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export const QuoteTableIncludedPerils = ({
  asset,
  branchId,
}: {
  asset: Asset<KeyValueFieldObject>;
  branchId?: string;
}) => {
  const { t } = useTranslation();

  const quoteTierModuleConfig = usePolicyProductConfig('quoteTierModuleConfig');

  const chosenTierForCustom = useMemo(
    () => asset?.quote?.options.tier as string | undefined,
    [asset],
  );

  if (quoteTierModuleConfig?.customIncludedPerils) {
    const CustomIncludedPerils = quoteTierModuleConfig.customIncludedPerils;
    return (
      <tr>
        <td colSpan={3} className="py-2">
          <CustomIncludedPerils tier={chosenTierForCustom} includedPerils={[]} />
        </td>
      </tr>
    );
  }

  const { policy } = usePolicyOnDashboard();
  const isGuaranteeExcessDisplayed = useHasPolicyProductConfig('isGuaranteeExcessDisplayed');
  const { data: config } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const chosenTier = useMemo(() => asset?.quote?.options.tier, [asset]);

  const availablePerilsConfig = useMemo(
    () => config?.quote?.availableConfigs.find(item => item.tier === chosenTier)?.perils,
    [config, chosenTier],
  );

  const perilCustomFieldsMap = useMemo(
    () =>
      Object.fromEntries(
        (availablePerilsConfig ?? []).flatMap(p => {
          const customFields = (p as { customFields?: string[] }).customFields;
          return customFields?.length ? [[p.id, customFields[0]]] : [];
        }),
      ),
    [availablePerilsConfig],
  );

  const includedPerilsInTiers = useMemo(() => {
    if (config?.usesQuoteConfig) {
      // Products with quoteConfig (e.g. assurequine): show config perils that are required or informational
      const configPerils = availablePerilsConfig ?? [];
      const assetPerils = asset?.quote?.options?.perils ?? [];
      return configPerils.filter(p => {
        if (p.required) return true;
        const assetPeril = assetPerils.find(ap => ap.id === p.id);
        return assetPeril === undefined;
      });
    }
    // Legacy products with individualPolicyConfig.quote: show only asset perils that are required in config
    return (
      asset?.quote?.options?.perils?.filter(
        peril => availablePerilsConfig?.find(element => element.id === peril.id)?.required ?? false,
      ) ?? []
    );
  }, [asset, availablePerilsConfig, config?.usesQuoteConfig]);

  const { data: wakamQuote } = useQuery({
    ...assetControllerGetAssetExternalQuoteQueryOptions(asset.id, { branchId }),
    enabled: quoteTierModuleConfig?.isExternalQuoteEnabled,
  });

  return includedPerilsInTiers.map((peril, index) => {
    let excess: string | undefined;
    const wakamData = wakamQuote?.data as {
      Garanties?: { CodeGarantie: string; Franchise: string }[];
    };
    if (wakamData?.Garanties) {
      excess = wakamData.Garanties.find((garantie: { CodeGarantie: string }) =>
        quoteTierModuleConfig.perilsMapping[peril.id]?.includes(garantie.CodeGarantie),
      )?.Franchise as string | undefined;
    }

    // No external quote for this product: the excess is declared per peril in the translations.
    if (isGuaranteeExcessDisplayed) {
      excess ??= t(`${policy?.productId}.perils.${peril.id}.excess`);
    }

    const assetPeril = asset?.quote?.options?.perils?.find(ap => ap.id === peril.id);
    const isUnavailable = Boolean(assetPeril?.unavailabilityMessage);

    const customFieldName =
      quoteTierModuleConfig?.perilCustomFieldsMapping?.[peril.id] ?? perilCustomFieldsMap[peril.id];
    const cfRaw = customFieldName
      ? (policy?.customFields as Record<string, unknown>)?.[customFieldName]
      : undefined;
    const rawValue = (assetPeril?.option ??
      (cfRaw !== undefined ? String(cfRaw) : undefined) ??
      ('options' in peril ? peril.options?.[0] : undefined)) as string | undefined;
    if (rawValue !== undefined && !isPerilValueIncluded(rawValue)) return null;
    const value = rawValue
      ? t([
          `${policy?.productId}.perils.${peril.id}.options.${rawValue}`,
          `options.${rawValue}`,
          rawValue,
        ])
      : undefined;

    const perilEmoji = t(`${policy?.productId}.perils.${peril.id}.emoji`, { defaultValue: '' });
    const perilIcon = isUnavailable ? (
      <div className="mr-3 h-4 w-4" />
    ) : perilEmoji ? (
      <span className="mr-3 shrink-0" aria-hidden="true">
        {perilEmoji}
      </span>
    ) : (
      <CheckIcon className="h-4 w-4 mr-3 shrink-0" />
    );

    return (
      <tr key={peril.id} className="text-sm hover:bg-gray-100">
        <td className="pr-12 font-semibold bg-white hidden md:table-cell">
          {!index && t('quoteTierModule.includedPerils')}
        </td>
        <td className="p-1">
          <Group gap={0} className={isUnavailable ? 'text-gray-400' : undefined}>
            {perilIcon}
            {t(`${policy?.productId}.perils.${peril.id}.label`, {
              value,
              count: Number(rawValue) || 0,
            })}
            {assetPeril?.unavailabilityMessage && (
              <Text inline size="xs" className="ml-2 text-red-600">
                {assetPeril.unavailabilityMessage}
              </Text>
            )}
          </Group>
        </td>
        <td className="p-1">
          <Group gap={0}>
            {Number.isNaN(Number(excess)) ? excess : formatPrice(Number(excess), 1)}
          </Group>
        </td>
      </tr>
    );
  });
};
